import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send, ArrowLeft, UploadCloud, X } from "lucide-react";
import { api, apiError, type Ticket as TicketType, type UserSummary } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GlassCard } from "../components/ui/GlassCard";
import { Spinner } from "../components/ui/FullScreenLoader";
import {
  PRIORITIES,
  CATEGORIES,
  PRIORITY_META,
  CATEGORY_META,
  PRIORITY_TEMPLATES,
  ISSUE_CATEGORIES,
} from "../lib/constants";
import { cn } from "../lib/cn";

const schema = z.object({
  title: z.string().trim().min(3, "Title must be at least 3 characters").max(255),
  description: z.string().trim().min(5, "Please describe the issue").max(10_000),
  priority: z.enum(["CRITICAL", "HIGH", "MEDIUM", "LOW"]).default("MEDIUM"),
  category: z
    .enum(["SYSTEM", "HARDWARE", "SOFTWARE", "NETWORK", "ACCESS", "OTHER", ""])
    .default(""),
  assigneeId: z.string().optional(),
});

type CreateForm = z.infer<typeof schema>;

interface Screenshot {
  id: string;
  file: File;
  previewUrl: string;
  name: string;
  size: number;
}

export default function CreateTicket() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, isStaff } = useAuth();

  const [screenshots, setScreenshots] = useState<Screenshot[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<CreateForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: "",
      description: "",
      priority: "MEDIUM",
      category: "",
      assigneeId: "",
    },
  });

  const priority = watch("priority");
  const category = watch("category");
  const description = watch("description");
  const [selectedIssue, setSelectedIssue] = useState("");
  const [customIssue, setCustomIssue] = useState("");

  const assigneesQ = useQuery({
    queryKey: ["users.assignees"],
    queryFn: async () => {
      const items = (await api.get<{ items: UserSummary[] }>("/api/users/assignees")).data.items;
      return items.filter((u) => u.role === "IT" || u.role === "ADMIN");
    },
  });

  const addFiles = (files: File[]) => {
    const validFiles = files.filter((file) => file.type.startsWith("image/"));
    if (validFiles.length !== files.length) {
      toast.error("Only image files are allowed as screenshots.");
    }

    const newScreenshots: Screenshot[] = validFiles.map((file) => ({
      id: Math.random().toString(36).substring(7),
      file,
      previewUrl: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
    }));

    setScreenshots((prev) => [...prev, ...newScreenshots]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    addFiles(files);
  };

  const removeScreenshot = (id: string) => {
    setScreenshots((prev) => {
      const target = prev.find((s) => s.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((s) => s.id !== id);
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    addFiles(files);
  };

  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item && item.type.indexOf("image") !== -1) {
          const file = item.getAsFile();
          if (file) {
            const pastedFile = new File(
              [file],
              `screenshot_${new Date().toISOString().replace(/[:.]/g, "-")}.png`,
              { type: file.type },
            );
            files.push(pastedFile);
          }
        }
      }
      if (files.length > 0) {
        addFiles(files);
        toast.success(`Pasted ${files.length} screenshot(s) from clipboard!`);
      }
    };

    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      screenshots.forEach((s) => URL.revokeObjectURL(s.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = useMutation({
    mutationFn: async (data: CreateForm & { issue?: string }) => {
      // 1. Create the ticket
      const ticket: TicketType = (await api.post("/api/tickets", data)).data
        .ticket;

      // 2. Upload screenshots sequentially if any
      if (screenshots.length > 0) {
        for (const s of screenshots) {
          try {
            const form = new FormData();
            form.append("file", s.file);
            await api.post(`/api/tickets/${ticket.id}/attachments`, form, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (err) {
            console.error(`Failed to upload screenshot: ${s.name}`, err);
            toast.error(`Failed to upload ${s.name}: ${apiError(err)}`);
          }
        }
      }
      return ticket;
    },
    onSuccess: (ticket) => {
      qc.invalidateQueries({ queryKey: ["dashboard.recent"] });
      qc.invalidateQueries({ queryKey: ["tickets.list"] });
      toast.success("Ticket created");
      navigate(`/tickets/${ticket.id}`);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const handleBack = () => {
    if (isDirty) {
      const confirmLeave = window.confirm(
        "You have unsaved changes. Are you sure you want to leave?",
      );
      if (!confirmLeave) return;
    }
    navigate(-1);
  };

  const handleCategoryClick = async (c: Exclude<CreateForm["category"], "">) => {
    // 1. Update the form value to select the category
    setValue("category", c, { shouldDirty: true });

    // 2. OTHER stays on form for normal submit
    if (c === "OTHER") {
      return;
    }

    // 3. Only IT and Admin users should navigate to specific trackers
    if (!isStaff) {
      return;
    }

    // 4. Trigger validation for Title and Description
    const isValid = await trigger(["title", "description"]);
    if (!isValid) {
      toast.error(
        "Please fill in Title and Description before selecting a category tracker.",
      );
      return;
    }

    // 5. Gather form values and navigate to tracker with state data passed forward
    const values = watch();
    const stateData = {
      title: values.title,
      description: values.description,
      priority: values.priority,
      category: c,
    };

    if (c === "HARDWARE") {
      navigate("/hardware-tracker", { state: stateData });
    } else if (c === "SYSTEM") {
      navigate("/system-tracker", { state: stateData });
    } else if (["SOFTWARE", "NETWORK", "ACCESS"].includes(c)) {
      navigate("/software-tracker", { state: stateData });
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <button onClick={handleBack} className="btn-ghost">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <GlassCard strong className="p-7">
        <h1 className="page-title">New Ticket</h1>
        <p className="page-subtitle">
          Describe the issue clearly. Agents will adjust priority if needed.
        </p>

        <form
          onSubmit={handleSubmit((d) => {
            const finalIssue = selectedIssue === "Others" ? customIssue.trim() : selectedIssue;
            const payload: any = { ...d, issue: finalIssue || undefined };
            if (!payload.assigneeId) {
              delete payload.assigneeId;
            }
            create.mutate(payload);
          })}
          className="mt-6 space-y-5"
        >
          <div>
            <label className="label">Title</label>
            <input
              className="input"
              placeholder="One-line summary, e.g. Cannot connect to VPN"
              {...register("title")}
            />
            {errors.title && <p className="field-error">{errors.title.message}</p>}
          </div>

          <div className="bg-white/[0.03] border border-white/10 p-3.5 rounded-xl flex items-start gap-3 transition-colors duration-300">
            <div className="mt-0.5 shrink-0">
              <span className={cn("w-2.5 h-2.5 rounded-full inline-block shadow-sm", PRIORITY_META[priority].dot)} />
            </div>
            <div>
              <div className={cn("text-[11px] font-bold tracking-wider uppercase mb-1", PRIORITY_META[priority].text)}>
                {PRIORITY_META[priority].label} Priority
              </div>
              <div className="text-[13px] text-slate-300 leading-relaxed">
                {PRIORITY_META[priority].description}
              </div>
            </div>
          </div>

          <div>
            <label className="label">Description (Input above 5 characters)</label>
            <textarea
              className="textarea"
              rows={6}
              placeholder="What did you try? What is the impact? Any error messages?"
              {...register("description")}
            />
            {errors.description && (
              <p className="field-error">{errors.description.message}</p>
            )}
          </div>

          <div>
            <label className="label flex items-center justify-between">
              <span>Screenshots (Optional)</span>
              {screenshots.length > 0 && (
                <span className="text-xs text-slate-400 font-medium">
                  {screenshots.length} screenshot{screenshots.length > 1 ? "s" : ""}{" "}
                  added
                </span>
              )}
            </label>

            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                "border border-dashed rounded-2xl p-6 transition-all duration-300 text-center cursor-pointer flex flex-col items-center justify-center gap-2 outline-none group",
                isDragging
                  ? "border-brand-400 bg-brand-500/10 scale-[1.01] shadow-glow"
                  : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20",
              )}
            >
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*"
                multiple
                onChange={handleFileChange}
              />

              <div className="w-12 h-12 rounded-xl bg-brand-500/15 border border-brand-400/30 flex items-center justify-center text-brand-300 mb-1 group-hover:scale-110 transition-transform duration-300">
                <UploadCloud className="w-6 h-6" />
              </div>

              <p className="text-sm font-medium text-white">
                Drag & drop screenshots here, or{" "}
                <span className="text-brand-400 font-semibold hover:underline">
                  browse
                </span>
              </p>
              <p className="text-xs text-slate-400">
                Supports PNG, JPG, JPEG, GIF. You can also paste directly using{" "}
                <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white font-mono text-[10px]">
                  Ctrl+V
                </kbd>{" "}
                anywhere!
              </p>
            </div>

            {screenshots.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
                {screenshots.map((s) => (
                  <div
                    key={s.id}
                    className="group relative rounded-xl border border-white/10 bg-white/[0.02] p-2 overflow-hidden transition-all duration-300 hover:border-white/20 hover:bg-white/[0.04]"
                  >
                    <div className="aspect-video w-full rounded-lg overflow-hidden bg-slate-950 relative border border-white/5">
                      <img
                        src={s.previewUrl}
                        alt={s.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeScreenshot(s.id);
                          }}
                          className="p-1.5 rounded-lg bg-critical-500/80 hover:bg-critical-500 text-white transition-all transform hover:scale-110"
                          title="Remove screenshot"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 px-1 text-left">
                      <div
                        className="text-xs text-white truncate font-medium"
                        title={s.name}
                      >
                        {s.name}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {(s.size / 1024).toFixed(1)} KB
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="label">Priority (suggested)</label>
              <div className="grid grid-cols-2 gap-2">
                {PRIORITIES.map((p) => {
                  const meta = PRIORITY_META[p];
                  const active = priority === p;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() =>
                        setValue("priority", p, { shouldDirty: true })
                      }
                      className={cn(
                        "rounded-xl px-3 py-2.5 text-left text-sm border transition-all duration-300 transform active:scale-95 hover:scale-[1.02]",
                        active
                          ? `${meta.bg} ${meta.border} ring-2 ${meta.ring} text-white font-semibold shadow-glow`
                          : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06] hover:border-white/20",
                      )}
                    >
                      <span className="inline-flex items-center gap-2">
                        <span className={cn("w-2 h-2 rounded-full", meta.dot)} />
                        {meta.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                An agent may adjust this based on impact.
              </p>

              {PRIORITY_TEMPLATES[priority]?.length > 0 && (
                <div className="mt-4">
                  <label className="label text-brand-300">Issue</label>
                  <select
                    className="select border-white/20 hover:border-white/30"
                    value={selectedIssue}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedIssue(val);
                      if (val && ISSUE_CATEGORIES[val]) {
                        setValue("category", ISSUE_CATEGORIES[val], { shouldDirty: true });
                      }
                    }}
                  >
                    <option value="">Select an issue...</option>
                    {PRIORITY_TEMPLATES[priority].map((tpl) => (
                      <option key={tpl} value={tpl}>{tpl}</option>
                    ))}
                    <option value="Others">Others</option>
                  </select>
                  {selectedIssue === "Others" && (
                    <input
                      type="text"
                      className="input mt-2"
                      placeholder="Type specific issue..."
                      value={customIssue}
                      onChange={(e) => setCustomIssue(e.target.value)}
                      required
                    />
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="label">Category</label>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORIES.map((c, i) => {
                  const active = category === c;
                  const isRight = i % 2 === 1;
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => handleCategoryClick(c)}
                      className={cn(
                        "group relative rounded-xl px-3 py-2.5 text-sm border transition-all text-left duration-300 transform active:scale-95 hover:scale-[1.02]",
                        active
                          ? "bg-brand-500/20 border-brand-400/50 text-white ring-2 ring-brand-400/30 shadow-glow font-semibold scale-[1.01]"
                          : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06] hover:border-white/20",
                      )}
                    >
                      {CATEGORY_META[c].label}
                      {CATEGORY_META[c].description && (
                        <div className={cn(
                          "absolute z-50 bottom-full mb-2 w-56 p-2.5 text-xs text-[#e2e8f0] bg-slate-800 border border-white/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-xl drop-shadow-2xl break-words whitespace-normal text-left hidden group-hover:block",
                          isRight ? "right-0" : "left-0"
                        )}>
                          {CATEGORY_META[c].description}
                          <div className={cn(
                            "absolute top-full border-[6px] border-transparent border-t-slate-800",
                            isRight ? "right-6" : "left-6"
                          )}></div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              {category && (
                <div className="mt-4 animate-in fade-in slide-in-from-top-2 duration-300">
                  <label className="label">IT Assigned (Optional)</label>
                  <select
                    className="select border-white/20 hover:border-white/30"
                    {...register("assigneeId")}
                  >
                    <option value="">Choose an Assignee</option>
                    {assigneesQ.data
                      ?.filter(
                        (a: UserSummary) =>
                          (a.department && a.department.toUpperCase() === category) ||
                          (a.additionalDepartments && a.additionalDepartments.some((d: string) => d.toUpperCase() === category))
                      )
                      .map((a: UserSummary) => {
                        const depts = [a.department, ...(a.additionalDepartments || [])].filter(Boolean);
                        return (
                          <option key={a.id} value={a.id}>
                            {a.name} {depts.length > 0 ? `(${depts.join(", ")})` : ""}
                          </option>
                        );
                      })}
                  </select>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Choose a specific agent or leave empty to auto-assign.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" className="btn-ghost" onClick={handleBack}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || create.isPending || !category}
              className={cn(
                "btn-primary transition-all duration-300",
                !category &&
                "opacity-50 cursor-not-allowed bg-slate-800 border-slate-700 text-slate-500 hover:bg-slate-800",
              )}
            >
              {create.isPending ? (
                <>
                  <Spinner />
                  {screenshots.length > 0 ? "Uploading..." : "Submitting..."}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Ticket
                </>
              )}
            </button>
          </div>
        </form>
      </GlassCard>
    </div>
  );
}
